import { Controller } from "@hotwired/stimulus"

/**
 * Notification Center Controller
 * 
 * Handles the notification center panel with filtering, 
 * marking read, dismissing, and settings management.
 */
export default class extends Controller {
  static targets = ["settings"]
  static values = {
    guestToken: String
  }

  connect() {
    this.activeCategory = "all"
    this.settingsOpen = false
  }

  // Filter notifications by category
  filterCategory(event) {
    const category = event.currentTarget.dataset.category
    this.activeCategory = category
    
    // Update tab states
    this.element.querySelectorAll(".notification-center__tab").forEach(tab => {
      tab.classList.toggle("notification-center__tab--active", tab.dataset.category === category)
      tab.setAttribute("aria-selected", tab.dataset.category === category)
    })
    
    // Filter items
    this.element.querySelectorAll(".notification-item").forEach(item => {
      const itemCategory = item.dataset.category
      const visible = category === "all" || itemCategory === category
      item.style.display = visible ? "" : "none"
    })
  }

  // Mark single notification as read
  async markRead(event) {
    const notificationId = event.currentTarget.dataset.notificationId
    const item = this.element.querySelector(`[data-notification-id="${notificationId}"]`)
    
    if (item) {
      item.classList.remove("notification-item--unread")
      const dot = item.querySelector(".notification-item__unread-dot")
      if (dot) dot.remove()
      
      // Hide the mark read button
      event.currentTarget.style.display = "none"
    }
    
    await this.sendAction("mark_read", { notification_ids: [notificationId] })
  }

  // Mark all notifications as read
  async markAllRead(event) {
    // Optimistically update UI
    this.element.querySelectorAll(".notification-item--unread").forEach(item => {
      item.classList.remove("notification-item--unread")
      const dot = item.querySelector(".notification-item__unread-dot")
      if (dot) dot.remove()
    })
    
    // Update badge
    const badge = this.element.querySelector(".notification-center__badge")
    if (badge) badge.remove()
    
    // Hide mark all button
    event.currentTarget.style.display = "none"
    
    await this.sendAction("mark_all_read", {})
  }

  // Dismiss a notification
  async dismiss(event) {
    const notificationId = event.currentTarget.dataset.notificationId
    const item = this.element.querySelector(`[data-notification-id="${notificationId}"]`)
    
    if (item) {
      // Animate out
      item.style.transition = "all 0.25s ease-out"
      item.style.transform = "translateX(100%)"
      item.style.opacity = "0"
      
      setTimeout(() => {
        item.remove()
        this.checkEmptyState()
      }, 250)
    }
    
    await this.sendAction("dismiss", { notification_id: notificationId })
  }

  // Handle action button click
  handleAction(event) {
    const actionUrl = event.currentTarget.dataset.actionUrl
    const notificationId = event.currentTarget.dataset.notificationId
    
    if (notificationId) {
      this.sendAction("mark_read", { notification_ids: [notificationId] })
    }
    
    if (actionUrl) {
      // Dispatch event for parent controller to handle navigation
      this.dispatch("action", { detail: { url: actionUrl } })
    }
  }

  // Toggle settings panel
  toggleSettings() {
    this.settingsOpen = !this.settingsOpen
    
    if (this.hasSettingsTarget) {
      this.settingsTarget.hidden = !this.settingsOpen
    }
  }

  // Save notification preferences
  async saveSettings() {
    const settings = {
      in_app: this.getCheckboxValue("in_app"),
      email: this.getCheckboxValue("email"),
      smart_grouping: this.getCheckboxValue("smart_grouping"),
      categories: this.getCategorySettings()
    }
    
    await this.sendAction("update_preferences", settings)
    this.toggleSettings()
  }

  // Helper: Get checkbox value
  getCheckboxValue(setting) {
    const checkbox = this.element.querySelector(`[data-setting="${setting}"]`)
    return checkbox ? checkbox.checked : true
  }

  // Helper: Get category settings
  getCategorySettings() {
    const settings = {}
    this.element.querySelectorAll("[data-category]").forEach(checkbox => {
      if (checkbox.type === "checkbox") {
        settings[checkbox.dataset.category] = { enabled: checkbox.checked }
      }
    })
    return settings
  }

  // Check if list is empty and show empty state
  checkEmptyState() {
    const items = this.element.querySelectorAll(".notification-item")
    const visibleItems = Array.from(items).filter(item => item.style.display !== "none")
    
    if (visibleItems.length === 0) {
      // Could show empty state, for now just let the template handle it on next refresh
    }
  }

  // Send action to server
  async sendAction(action, payload) {
    try {
      const response = await fetch(`/carta/${this.guestTokenValue}/notifications/${action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(payload)
      })
      
      if (!response.ok) {
        console.error("Notification action failed:", response.statusText)
      }
    } catch (error) {
      console.error("Notification action error:", error)
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
